import { Link } from 'react-router-dom';
import type { InvoiceSummary } from '@portal-contract';
import { TableBase, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@/lib/macondo';
import { useI18n } from '@/i18n/I18nProvider';
import { useIsWide } from '@/hooks/useMediaQuery';
import { formatDate, formatMoney } from '@/lib/format';
import { Card } from './Card';
import { StatusBadge } from './StatusBadge';

const invoicePath = (cid: string, iid: string): string => `/a/${encodeURIComponent(cid)}/invoices/${encodeURIComponent(iid)}`;

/** Invoices as a table from 640 px and as cards below it. Each invoice number links to its detail page. */
export function InvoiceList({ items, cid, caption }: { items: InvoiceSummary[]; cid: string; caption: string }) {
  const { t, lang } = useI18n();
  const wide = useIsWide();

  if (wide) {
    return (
      <Card className="overflow-hidden">
        <TableBase>
          <TableCaption className="sr-only">{caption}</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">{t('invoices.col.number')}</TableHead>
              <TableHead scope="col">{t('invoices.col.date')}</TableHead>
              <TableHead scope="col">{t('invoices.col.due')}</TableHead>
              <TableHead scope="col" className="text-right">
                {t('invoices.col.total')}
              </TableHead>
              <TableHead scope="col" className="text-right">
                {t('invoices.col.balance')}
              </TableHead>
              <TableHead scope="col">{t('invoices.col.status')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id}>
                <TableCell className="font-medium">
                  <Link to={invoicePath(cid, i.id)} className="focus-ring text-typography-primary inline-flex min-h-[44px] items-center underline">
                    {i.docNumber}
                  </Link>
                </TableCell>
                <TableCell>{formatDate(i.txnDate, lang)}</TableCell>
                <TableCell>{i.dueDate ? formatDate(i.dueDate, lang) : '-'}</TableCell>
                <TableCell className="text-right tabular-nums">{formatMoney(i.total, lang)}</TableCell>
                <TableCell className="text-right font-medium tabular-nums">{formatMoney(i.balance, lang)}</TableCell>
                <TableCell>
                  <StatusBadge invoice={i} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableBase>
      </Card>
    );
  }

  return (
    <ul className="flex flex-col gap-300" aria-label={caption}>
      {items.map((i) => (
        <li key={i.id}>
          <Card className="p-400">
            <div className="flex flex-col items-start gap-100">
              <Link to={invoicePath(cid, i.id)} className="focus-ring text-headline text-typography-primary inline-flex min-h-[44px] items-center underline">
                {i.docNumber}
              </Link>
              <StatusBadge invoice={i} />
            </div>
            <dl className="text-callout mt-100 grid grid-cols-2 gap-x-400 gap-y-200">
              <Pair label={t('invoices.col.date')} value={formatDate(i.txnDate, lang)} />
              <Pair label={t('invoices.col.due')} value={i.dueDate ? formatDate(i.dueDate, lang) : '-'} />
              <Pair label={t('invoices.col.total')} value={formatMoney(i.total, lang)} />
              <Pair label={t('invoices.col.balance')} value={formatMoney(i.balance, lang)} strong />
            </dl>
          </Card>
        </li>
      ))}
    </ul>
  );
}

export function Pair({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-footnote text-typography-secondary">{label}</dt>
      <dd className={`${strong ? 'text-callout-semibold' : 'text-callout'} text-typography-primary tabular-nums`}>{value}</dd>
    </div>
  );
}
